import { useEffect, useRef, useState } from 'react';
import type { DefinedInitialDataOptions } from '@tanstack/react-query';
import type { IxmEventsV1PollServiceReceiveReceiveRequest, IxmEventsV1PollServiceReceiveResponse } from '@interxfi/types/generated/api';
import { stableValueHash } from '@interxfi/utilities/hash/stable-value-hash';
import { useIxmEventsV1PollServiceReceive } from './generated/hooks/ixm-events-v1-poll-service-receive';
import { useProcessPSMEvent } from './query-client/hooks/use-psm-event-processor';
import type { PSMEvent } from './query-client/types';
import { TenantContext, useActiveRealmAPIContext } from './realm-context';

const initialSince = new Date().toISOString();
const PAGE_SIZE = '30';
const POLLING_WAIT_TIME_IN_SECONDS = 30;

const requestOptions: Partial<DefinedInitialDataOptions<IxmEventsV1PollServiceReceiveResponse | undefined>> = {
  refetchInterval: false,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
};

function buildRequest(since: string | undefined, nextToken: string | undefined): IxmEventsV1PollServiceReceiveReceiveRequest {
  const baseRequest: IxmEventsV1PollServiceReceiveReceiveRequest = {
    maxWaitTimeSeconds: POLLING_WAIT_TIME_IN_SECONDS,
    page: {
      pageSize: PAGE_SIZE,
      token: nextToken === '0' ? undefined : nextToken,
    },
  };

  if (nextToken && nextToken !== '0') {
    return baseRequest;
  }

  return {
    ...baseRequest,
    since,
  };
}

function getMostRecentEventForEachEntity(events: PSMEvent[]): Record<string, PSMEvent> {
  return events.reduce<Record<string, PSMEvent>>((acc, curr) => {
    const currentStateType = curr.entityState['!type'];
    const keyHash = stableValueHash(curr.entityKeys);
    const keyName = `${currentStateType}-${keyHash}`;

    const existingState = acc[keyName];
    if (!existingState) {
      acc[keyName] = curr;
      return acc;
    }

    const currEventDate = new Date(curr.timestamp);
    const existingEventDate = new Date(existingState.timestamp);

    if (currEventDate === existingEventDate) {
      acc[keyName] = (curr.sequence ?? '0') > (existingState.sequence ?? '0') ? curr : existingState;
    } else if (currEventDate > existingEventDate) {
      acc[keyName] = curr;
    }

    return acc;
  }, {});
}

export function useEventPoller() {
  const apiContext = useActiveRealmAPIContext();
  const lastPolledRealmAccessId = useRef<string | undefined>(apiContext.activeAccess?.realmAccessId);
  const shouldPoll = apiContext.activeTenantContext && [TenantContext.Program, TenantContext.Clearing].includes(apiContext.activeTenantContext);
  const [request, setRequest] = useState<IxmEventsV1PollServiceReceiveReceiveRequest>(() => buildRequest(initialSince, undefined));
  const handleProcessEvent = useProcessPSMEvent();
  const { data, dataUpdatedAt } = useIxmEventsV1PollServiceReceive(shouldPoll ? request : undefined, requestOptions);

  useEffect(() => {
    if (shouldPoll && apiContext?.activeAccess?.realmAccessId !== lastPolledRealmAccessId.current) {
      setRequest(buildRequest(new Date().toISOString(), undefined));
    }
  }, [shouldPoll, apiContext?.activeAccess?.realmAccessId]);

  useEffect(() => {
    if (data) {
      const { events, page } = data;
      const { nextToken } = page || {};

      if (events) {
        const dedupedEvents = Object.values(getMostRecentEventForEachEntity(events));
        for (const mostRecentEntityEvent of dedupedEvents) {
          handleProcessEvent(mostRecentEntityEvent);
        }
      }

      // Update the request parameters with the new pagination token, or if it hasn't changed, update "since", in order to restart the polling
      setRequest(buildRequest(new Date(dataUpdatedAt).toISOString(), nextToken));
    }
  }, [data, dataUpdatedAt, handleProcessEvent]);
}
